// Authentication state for the admin console. Holds the bearer token + the
// signed-in admin, persists them to localStorage, and keeps the api layer's
// token holder in sync so every request carries `Authorization`. One feature
// per file (see src/store/index.ts).
import { create } from "zustand";
import { persist } from "zustand/middleware";

import { api, setAuthToken, setSessionExpiredHandler, type AuthUser } from "@/api";
import { queryClient } from "@/providers/query-provider";

// `loading` is the boot state while we revalidate a persisted token against
// /auth/me; the route guard renders a spinner until it resolves to one of the
// terminal states.
export type AuthStatus = "loading" | "authenticated" | "anonymous";

type AuthState = {
	user: AuthUser | null;
	token: string | null;
	status: AuthStatus;
	signIn: (token: string, user: AuthUser) => void;
	signOut: () => Promise<void>;
	bootstrap: () => Promise<void>;
};

export const useAuth = create<AuthState>()(
	persist(
		(set, get) => ({
			user: null,
			token: null,
			status: "loading",

			signIn: (token, user) => {
				setAuthToken(token);
				set({ token, user, status: "authenticated" });
			},

			signOut: async () => {
				// Best-effort server-side revocation; clear locally regardless.
				try {
					await api.auth.logout();
				} catch {
					// ignore — the session is being discarded either way
				}
				setAuthToken(null);
				queryClient.clear();
				set({ token: null, user: null, status: "anonymous" });
			},

			// Runs once on app start. With no persisted token we're anonymous;
			// otherwise we revalidate it against the backend and either confirm
			// the session or drop it.
			bootstrap: async () => {
				const { token } = get();
				if (!token) {
					set({ status: "anonymous" });
					return;
				}
				setAuthToken(token);
				try {
					const user = await api.auth.me();
					// /auth/me is shared with the seller app; only full admins or users
					// with configured platform console access may unlock this app.
					const canAccessConsole =
						user.role === "admin" || user.effectivePermissions?.includes("console.access");
					if (!canAccessConsole) throw new Error("No admin-console access");
					set({ user, status: "authenticated" });
				} catch {
					setAuthToken(null);
					set({ token: null, user: null, status: "anonymous" });
				}
			},
		}),
		{
			name: "osmix-admin-auth",
			version: 1,
			// Persist only the credential pair; `status` is always recomputed by
			// bootstrap() so a reload never trusts a stale "authenticated".
			partialize: (state) => ({ token: state.token, user: state.user }),
			// Sync the rehydrated token into the api layer immediately, before
			// bootstrap runs, so any early request is already authenticated.
			onRehydrateStorage: () => (state) => {
				if (state?.token) setAuthToken(state.token);
			},
		},
	),
);

// Истёкший вход (401 на любой запрос с текущим токеном): сбрасываем сессию
// локально — без запроса logout, токен на шлюзе и так недействителен. Охрана
// маршрутов сама переведёт на /login.
setSessionExpiredHandler(() => {
	setAuthToken(null);
	queryClient.clear();
	useAuth.setState({ token: null, user: null, status: "anonymous" });
});
